export type Method = 'get' | 'post' | 'put';

/** HTTP verb passed to `fetch` (includes semantic GET-via-POST). */
type FetchMethod = Method | 'getPost';

export type ApiRequester = <TData>(url: string, sendParams?: string | object | undefined) => Promise<TData>;

/**
 * getPost means stuff that are semantically `Get` requests but we do them as `Post`
 */
export type ApiRequesterClient = Record<Method | 'getPost', ApiRequester>;

const buildFullUrl = (url: string) => {
  return `/higherlogic/ocapi/oc${url}`;
};

export const getRequestVerificationToken = () => {
  const formTokenElement = document.getElementsByName('__HL-RequestVerificationToken')[0] as HTMLInputElement;
  const formToken = formTokenElement ? formTokenElement.value : '';
  return formToken;
};

const buildOcApiRequester = (method: FetchMethod): ApiRequester => {
  return async <TData>(url: string, sendParams?: string | object | undefined): Promise<TData> => {
    try {
      const fullUrl = buildFullUrl(url);
      const formToken = getRequestVerificationToken();

      const requestOptions: RequestInit = {
        method: method === 'getPost' ? 'POST' : method.toUpperCase(),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          RequestVerificationFormToken: formToken
        }
      };

      if (sendParams && method !== 'get') {
        requestOptions.body = JSON.stringify(sendParams);
      }

      const response = await fetch(fullUrl, requestOptions);

      if (!response.ok) {
        const errorText = await response.text();
        const cleanError = errorText.replace(/<br \/>/g, ' ').replace(/<br>/g, ' ');
        throw new Error(cleanError || `HTTP ${response.status}: ${response.statusText}`);
      }

      const body = (await response.json()) as { data?: TData } | undefined;

      if (!body || body.data === undefined) {
        console.error(`Malformed response body from ${url} :`, body);
        throw new Error(`Malformed response body`);
      }

      return body.data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred');
    }
  };
};

export const OcApiRequest: ApiRequesterClient = {
  get: buildOcApiRequester('get'),
  getPost: buildOcApiRequester('getPost'),
  post: buildOcApiRequester('post'),
  put: buildOcApiRequester('put')
};

export default OcApiRequest;
